import React, { useCallback, useEffect, useState } from 'react';
import { Badge, Banner, BlockStack, Box, Button, Card, Checkbox, IndexTable, InlineStack, Modal, Select, Text, TextField } from '@shopify/polaris';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthComponent';
import { reviewPath } from './routes';
import { cacApi } from './apiClient';

const when = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' }) : '');
const TONE = { completed: 'success', running: 'attention', queued: 'info', failed: 'critical' };
const live = (r) => r.status === 'running' || r.status === 'queued';
const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

export function scopeText(run) {
  const o = run.options || {};
  if (run.kind === 'import') {
    const s = o.scope || {};
    return `${(s.statuses || ['ACTIVE']).join(', ')} · Team Portal ${s.include_team_portal ? 'included' : 'excluded'}${s.in_stock_only ? ' · in stock only' : ''}`;
  }
  if (run.kind === 'rewrite') {
    const verb = o.action === 'rename' ? `rename ${o.facet} "${o.old}" to "${o.new}"` : o.action === 'remove' ? `remove ${o.facet} "${o.old}"` : `write ${o.facet} "${o.old}" as "${o.new}"`;
    return `Dictionary: ${verb} on every product that holds it`;
  }
  const ai = o.ai ? `${o.layers === 'all' ? ' · AI (layers all)' : ' · AI'}${o.batch ? ', batched' : ''}` : '';
  if (o.product_ids) return `${o.product_ids.length} products${ai}`;
  if (o.filter) return `a filtered set of products${ai}`;
  return `everything without a proposal${ai}`;
}

function NewRunModal({ onClose, onQueued, notify }) {
  const [kind, setKind] = useState('analyze');
  const [statuses, setStatuses] = useState({ ACTIVE: true, UNLISTED: false, DRAFT: false });
  const [portal, setPortal] = useState(false);
  const [stock, setStock] = useState(false);
  const [ceiling, setCeiling] = useState('10');
  const [useAi, setUseAi] = useState(true);
  const [layers, setLayers] = useState('auto');
  const [how, setHow] = useState('batch');   // immediate | batch
  const isAnalyze = kind === 'analyze' || kind === 'reanalyze';
  const aiNeedsCeiling = isAnalyze && useAi && ceiling.trim() === '';
  const changeKind = (k) => { setKind(k); setLayers(k === 'reanalyze' ? 'all' : 'auto'); };
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const queue = async () => {
    setBusy(true); setError('');
    const options = kind === 'import'
      ? { scope: { statuses: Object.keys(statuses).filter((s) => statuses[s]), include_team_portal: portal, in_stock_only: stock } }
      : kind === 'analyze' ? { all_without_proposal: true, ai: useAi, layers } : { ai: useAi, layers };
    if (isAnalyze && useAi && how === 'batch') options.batch = true;
    try {
      await cacApi.startRun(kind, options, ceiling === '' ? null : Number(ceiling));
      notify(`Queued ${kind} run`); onQueued();
    } catch (e) { setError(e.message); setBusy(false); }
  };
  return (
    <Modal open onClose={onClose} title="New run" primaryAction={{ content: 'Queue run', onAction: queue, loading: busy, disabled: (kind === 'import' && !Object.values(statuses).some(Boolean)) || aiNeedsCeiling }}
      secondaryActions={[{ content: 'Cancel', onAction: onClose }]}>
      <Modal.Section>
        <BlockStack gap="300">
          {error && <Banner tone="critical"><p>{error}</p></Banner>}
          <Select label="Kind" value={kind} onChange={changeKind} options={[{ value: 'import', label: 'Import' }, { value: 'analyze', label: 'Analyze' }, { value: 'reanalyze', label: 'Re-analyze' }]} />
          {kind === 'import' && (
            <BlockStack gap="100">
              <Text as="p" fontWeight="semibold">Scope</Text>
              <InlineStack gap="300">{Object.keys(statuses).map((s) => <Checkbox key={s} label={s[0] + s.slice(1).toLowerCase()} checked={statuses[s]} onChange={(v) => setStatuses({ ...statuses, [s]: v })} />)}</InlineStack>
              <Checkbox label="Include Team Portal products" checked={portal} onChange={setPortal} />
              <Checkbox label="In-stock only" checked={stock} onChange={setStock} />
            </BlockStack>
          )}
          {isAnalyze && (
            <BlockStack gap="200">
              <Checkbox label="Use AI (image, vendor pages, category pick)" checked={useAi} onChange={setUseAi} />
              {useAi && <Select label="Layers" value={layers} onChange={setLayers} options={[
                { value: 'auto', label: 'auto — only what the catalog left unsettled' },
                { value: 'all', label: 'all — every layer, even where the catalog is sure' }]} />}
              {useAi && <Select label="How" value={how} onChange={setHow} options={[
                { value: 'batch', label: 'Batch — half price, results within hours (24 at most)' },
                { value: 'immediate', label: 'Immediate — one product at a time, full price' }]}
                helpText="A batched run asks for all the images at once, then the vendor pages, then the category picks; the worker checks on each batch every few minutes and other runs are not held up. The ceiling is checked before each batch is sent, so one batch can take a run past it by its own cost. Nothing is written to Shopify either way." />}
            </BlockStack>
          )}
          <TextField label="Budget ceiling $" type="number" value={ceiling} onChange={setCeiling} autoComplete="off" min={0} helpText={'Runs in the background worker; cancel any time. A run stops at the ceiling with what it has.'} error={aiNeedsCeiling ? 'AI runs need a ceiling' : undefined} />
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}

export default function RunsTab({ notify }) {
  const { nameOf } = useAuth();
  const [runs, setRuns] = useState(null);
  const navigate = useNavigate();
  const openRun = (r) => { if (r.kind !== 'import') navigate(reviewPath(`run=${r.id}`)); };
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const load = useCallback(() => cacApi.runs().then((r) => setRuns(r.runs)).catch((e) => setError(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const anyLive = (runs || []).some(live);
  useEffect(() => { if (!anyLive) return undefined; const t = setInterval(load, 5000); return () => clearInterval(t); }, [anyLive, load]);
  const cancel = async (r) => { try { await cacApi.cancelRun(r.id); notify('Cancel requested'); load(); } catch (e) { notify(e.message, true); } };

  return (
    <Card padding="0">
      <Box padding="400"><InlineStack align="space-between" blockAlign="center"><Text as="h3" variant="headingSm">Runs</Text><Button variant="primary" onClick={() => setCreating(true)}>New run…</Button></InlineStack></Box>
      {error && <Banner tone="critical"><p>{error}</p></Banner>}
      <IndexTable resourceName={{ singular: 'run', plural: 'runs' }} itemCount={(runs || []).length} selectable={false} loading={!runs && !error}
        headings={[{ title: 'Kind' }, { title: 'Scope' }, { title: 'Status' }, { title: 'Progress' }, { title: 'Cost' }, { title: 'Started by' }, { title: 'When' }, { title: 'Note' }, { title: '' }]}
        emptyState={<Box padding="400"><Text as="p" tone="subdued">No runs yet.</Text></Box>}>
        {(runs || []).map((r, i) => (
          <IndexTable.Row id={r.id} key={r.id} position={i} onClick={() => openRun(r)}>
            <IndexTable.Cell>{r.kind}</IndexTable.Cell>
            <IndexTable.Cell>{scopeText(r)}</IndexTable.Cell>
            <IndexTable.Cell><Badge tone={TONE[r.status]}>{r.status}</Badge></IndexTable.Cell>
            <IndexTable.Cell>{r.kind !== 'import' ? <Button variant="plain" onClick={() => openRun(r)}>{`${r.product_count != null ? `${(r.done_count || 0).toLocaleString()} / ${r.product_count.toLocaleString()}` : (r.done_count || 0).toLocaleString()}${r.failed_count ? ` · ${r.failed_count} failed` : ''}`}</Button> : `${(r.done_count || 0).toLocaleString()}`}</IndexTable.Cell>
            <IndexTable.Cell>{money(r.cost_cents)}{r.ceiling_cents != null ? ` of ${money(r.ceiling_cents)}` : ''}</IndexTable.Cell>
            <IndexTable.Cell>{nameOf(r.started_by) || ''}</IndexTable.Cell>
            <IndexTable.Cell>{when(r.started_at || r.queued_at)}</IndexTable.Cell>
            <IndexTable.Cell><Text as="span" variant="bodySm" breakWord>{r.note || ''}</Text></IndexTable.Cell>
            <IndexTable.Cell>{r.kind !== 'import' && live(r) && !r.cancel_requested ? <Button tone="critical" onClick={() => cancel(r)}>Cancel</Button> : r.kind !== 'import' && r.cancel_requested && live(r) ? <Text as="span" tone="subdued">Cancelling…</Text> : null}</IndexTable.Cell>
          </IndexTable.Row>
        ))}
      </IndexTable>
      {creating && <NewRunModal onClose={() => setCreating(false)} notify={notify} onQueued={() => { setCreating(false); load(); }} />}
    </Card>
  );
}
